import { normalizeSportName } from './drillTemplates';
import {
  CORE_ANALYSIS_FRAMEWORK_ID,
  SAS_SNOWBOARD_ANALYSIS_VARIANT,
  SAS_SKI_ANALYSIS_VARIANT,
  getAnalysisDisplayMeta,
  getVariantOptionLabel,
  getVariantOptions,
  getAnalysisVariantForSport,
  createEmptyAnalysisForVariant,
} from './analysisSchema';

export { CORE_ANALYSIS_FRAMEWORK_ID, SAS_SNOWBOARD_ANALYSIS_VARIANT, SAS_SKI_ANALYSIS_VARIANT };
export { getAnalysisDisplayMeta };

export const SNOWBOARD_INEFFICIENCY_OPTIONS = getVariantOptions(SAS_SNOWBOARD_ANALYSIS_VARIANT, 'inefficiency');
export const SNOWBOARD_BOARD_PERFORMANCE_OPTIONS = getVariantOptions(SAS_SNOWBOARD_ANALYSIS_VARIANT, 'boardPerformance');
export const SKI_PRIMARY_PERFORMANCE_OPTIONS = getVariantOptions(SAS_SKI_ANALYSIS_VARIANT, 'primaryPerformance');
export const SKI_PERFORMANCE_OPTIONS = getVariantOptions(SAS_SKI_ANALYSIS_VARIANT, 'performance');

export const SNOWBOARD_INEFFICIENCY_TAGS = SNOWBOARD_INEFFICIENCY_OPTIONS.map((item) => item.value);
export const SNOWBOARD_BOARD_PERFORMANCE_ISSUES = SNOWBOARD_BOARD_PERFORMANCE_OPTIONS.map((item) => item.value);
export const SKI_PRIMARY_PERFORMANCE_ISSUES = SKI_PRIMARY_PERFORMANCE_OPTIONS.map((item) => item.value);
export const SKI_PERFORMANCE_ISSUES = SKI_PERFORMANCE_OPTIONS.map((item) => item.value);

const SKI_ISSUE_LABELS = {
  aft_balance: 'Aft balance',
  upper_body_rotation: 'Upper body rotation',
  weak_outside_ski: 'Weak outside ski',
  limited_ankle_flexion: 'Limited ankle flexion',
  late_edge_change: 'Late edge change',
  pressure_management: 'Pressure management',
  inconsistent_turn_shape: 'Inconsistent turn shape',
  unfinished_turn: 'Unfinished turn',
  skidding: 'Skidding',
  speed_control_issue: 'Speed control',
  lack_of_flow: 'Lack of flow',
  late_pressure_transfer: 'Late pressure transfer',
  lateral_balance: 'Lateral balance',
  excessive_rotary_input: 'Excessive rotary input',
};

const SKI_GOAL_LABELS = {
  quiet_upper_body: 'Quieter upper body',
  earlier_ankle_flexion: 'Earlier ankle flexion',
  rounder_turn_shape: 'Rounder turn shape',
  earlier_pressure_build: 'Earlier pressure build',
  stronger_turn_finish: 'Stronger turn finish',
  stronger_outside_ski_balance: 'Stronger outside ski balance',
  smoother_edge_change: 'Smoother edge change',
};

const SKI_STRENGTH_LABELS = {
  confidence_and_control: 'Confidence and control',
  comfort_on_blue_groomers: 'Comfort on blue groomers',
  comfort_on_green_groomers: 'Comfort on green groomers',
  comfort_on_red_terrain: 'Comfort on red terrain',
  commitment_to_outside_ski: 'Commitment to the outside ski',
};

const SNOWBOARD_ISSUE_LABELS = {
  counter_rotation: 'Counter-rotation',
  back_foot_dominance: 'Back-foot dominance',
  limited_edge_commitment: 'Limited edge commitment',
  stiff_ankles: 'Stiff ankles',
  disconnected_upper_lower_body: 'Disconnected upper/lower body',
  late_transition: 'Late transition',
  heel_toe_asymmetry: 'Heel/toe asymmetry',
  speed_control_issue: 'Speed control',
  inconsistent_track: 'Inconsistent track',
  weak_lower_body_steering: 'Weak lower-body steering',
  limited_terrain_adaptation: 'Limited terrain adaptation',
};

const SNOWBOARD_GOAL_LABELS = {
  quieter_upper_body: 'Quieter upper body',
  patient_transition: 'Patient transition',
  stacked_alignment: 'Stacked alignment',
  stronger_edge_commitment: 'Stronger edge commitment',
  centered_stance: 'More centered stance',
  lower_body_steering: 'Lower-body steering',
  rounder_turn_shape: 'Rounder turn shape',
  terrain_adaptation_confidence: 'Terrain adaptation confidence',
  smoother_pressure_management: 'Smoother pressure management',
};

const SNOWBOARD_STRENGTH_LABELS = {
  confidence_and_control: 'Confidence and control',
  balanced_edge_change: 'Balanced edge change',
  calm_board_flow: 'Calm board flow',
  centered_mobile_position: 'Centered mobile position',
  terrain_adaptation_confidence: 'Terrain adaptation confidence',
};

const SNOWBOARD_SIGNAL_ALIASES = {
  goals: {
    quiet_upper_body: 'quieter_upper_body',
  },
};

function cleanArray(values = []) {
  return (values || []).map((item) => String(item || '').trim()).filter(Boolean);
}

function cleanObjectValues(value = {}) {
  return Object.fromEntries(
    Object.entries(value || {}).map(([key, item]) => [key, String(item || '').trim()])
  );
}

function slugify(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function unique(values = []) {
  return Array.from(new Set((values || []).filter(Boolean)));
}

function includesKeyword(text, keywords = []) {
  return keywords.some((keyword) => text.includes(keyword));
}

function detectGoalThemes(text) {
  const normalized = String(text || '').toLowerCase();
  const matches = [];

  if (includesKeyword(normalized, ['upper body', 'torso', 'shoulder', 'quiet'])) {
    matches.push('quiet_upper_body');
  }
  if (includesKeyword(normalized, ['ankle', 'shin', 'flex'])) {
    matches.push('earlier_ankle_flexion');
  }
  if (includesKeyword(normalized, ['round', 'turn shape', 'line'])) {
    matches.push('rounder_turn_shape');
  }
  if (includesKeyword(normalized, ['pressure', 'outside ski', 'build earlier'])) {
    matches.push('earlier_pressure_build');
  }
  if (includesKeyword(normalized, ['finish', 'complete the turn'])) {
    matches.push('stronger_turn_finish');
  }
  if (includesKeyword(normalized, ['outside ski', 'platform'])) {
    matches.push('stronger_outside_ski_balance');
  }
  if (includesKeyword(normalized, ['edge', 'release', 'transition'])) {
    matches.push('smoother_edge_change');
  }
  if (includesKeyword(normalized, ['patient', 'calm transition'])) {
    matches.push('patient_transition');
  }
  if (includesKeyword(normalized, ['stacked', 'aligned'])) {
    matches.push('stacked_alignment');
  }
  if (includesKeyword(normalized, ['centered', 'center'])) {
    matches.push('centered_stance');
  }
  if (includesKeyword(normalized, ['commit to the edge', 'edge commitment'])) {
    matches.push('stronger_edge_commitment');
  }
  if (includesKeyword(normalized, ['steer with the legs', 'lower body steering', 'steer from the lower body'])) {
    matches.push('lower_body_steering');
  }
  if (includesKeyword(normalized, ['rounder turn shape', 'round turn shape', 'rounder arc'])) {
    matches.push('rounder_turn_shape');
  }
  if (includesKeyword(normalized, ['terrain adaptation', 'terrain confidence', 'adapt to terrain', 'variable terrain', 'easy variable terrain'])) {
    matches.push('terrain_adaptation_confidence');
  }
  if (includesKeyword(normalized, ['pressure management', 'manage pressure', 'smoother pressure'])) {
    matches.push('smoother_pressure_management');
  }

  return unique(matches);
}

function detectTerrainComfortKey(value) {
  const normalized = slugify(value);
  if (!normalized) return '';
  if (normalized.includes('green')) return 'green_groomers';
  if (normalized.includes('blue')) return 'blue_groomers';
  if (normalized.includes('red')) return 'red_terrain';
  if (normalized.includes('black')) return 'black_terrain';
  return normalized;
}

function getLabelFromMap(map, value) {
  return map[value] || String(value || '').replace(/_/g, ' ');
}

function buildSignalList(keys, labelMap) {
  return unique(keys).map((key) => ({
    key,
    label: getLabelFromMap(labelMap, key),
  }));
}

function normalizeSignalEntries(entries = [], labelMap, aliases = {}) {
  const normalized = [];

  (entries || []).forEach((entry) => {
    const rawKey = typeof entry === 'string' ? entry : entry?.key;
    const key = aliases[rawKey] || rawKey;
    if (!key) return;
    normalized.push({
      key,
      label: getLabelFromMap(labelMap, key),
    });
  });

  return unique(normalized.map((item) => item.key)).map((key) => ({
    key,
    label: getLabelFromMap(labelMap, key),
  }));
}

function normalizeSkiIssues(analysis) {
  const issues = [];

  (analysis.detect?.primaryPerformanceIssues || []).forEach((item) => {
    const mapping = {
      late_edge_engagement: 'late_edge_change',
      weak_outside_ski: 'weak_outside_ski',
      inconsistent_turn_shape: 'inconsistent_turn_shape',
      speed_control_issue: 'speed_control_issue',
      skidding: 'skidding',
      lack_of_flow: 'lack_of_flow',
      late_pressure_transfer: 'late_pressure_transfer',
      unfinished_turn: 'unfinished_turn',
    };
    if (mapping[item]) issues.push(mapping[item]);
  });

  (analysis.effect?.skiPerformanceIssues || []).forEach((item) => {
    const mapping = {
      late_edge_set: 'late_edge_change',
      washed_out_finish: 'unfinished_turn',
      inside_ski_dominance: 'weak_outside_ski',
      weak_platform: 'weak_outside_ski',
      limited_grip: 'late_edge_change',
      inconsistent_radius: 'inconsistent_turn_shape',
      skidding: 'skidding',
      speed_runaway: 'speed_control_issue',
    };
    if (mapping[item]) issues.push(mapping[item]);
  });

  const stanceIssues = analysis.cause?.stanceIssues || {};
  if (stanceIssues.ankles) issues.push('limited_ankle_flexion');
  if (stanceIssues.upperBody) issues.push('upper_body_rotation');
  if (stanceIssues.foreAft) issues.push('aft_balance');
  if (stanceIssues.lateralBalance) issues.push('lateral_balance');
  if (stanceIssues.rotaryInput) issues.push('excessive_rotary_input');
  if (stanceIssues.pressureManagement) issues.push('pressure_management');

  return unique(issues);
}

function normalizeSnowboardIssues(analysis) {
  const issues = [];
  (analysis.detect?.observedInefficiencies || []).forEach((item) => {
    const mapping = {
      edge_preference: 'heel_toe_asymmetry',
      late_transition: 'late_transition',
      counter_rotation: 'counter_rotation',
      aft_stance: 'back_foot_dominance',
      excessive_lateral: 'disconnected_upper_lower_body',
      jerky_upper_body: 'disconnected_upper_lower_body',
      inconsistent_track: 'inconsistent_track',
      open_turns: 'limited_edge_commitment',
    };
    if (mapping[item]) issues.push(mapping[item]);
  });

  (analysis.effect?.boardPerformanceIssues || []).forEach((item) => {
    const mapping = {
      late_edge: 'late_transition',
      speed_control_issue: 'speed_control_issue',
      inconsistent_track: 'inconsistent_track',
      chatter: 'limited_edge_commitment',
      skid: 'limited_edge_commitment',
      abrupt_edge_change: 'late_transition',
      loss_of_momentum: 'back_foot_dominance',
      open_turns: 'limited_edge_commitment',
    };
    if (mapping[item]) issues.push(mapping[item]);
  });

  const stanceIssues = analysis.cause?.stanceIssues || {};
  if (stanceIssues.foreAft) issues.push('back_foot_dominance');
  if (stanceIssues.upperBody || stanceIssues.rotational) issues.push('counter_rotation');
  if (stanceIssues.ankles) issues.push('stiff_ankles');
  if (stanceIssues.knees || stanceIssues.hips) issues.push('weak_lower_body_steering');

  const text = [
    analysis.detect?.bigPictureIssue,
    analysis.detect?.edgePressureSteerObservation,
    analysis.cause?.movementDescription,
    analysis.effect?.effectDescription,
    analysis.correct?.movementCorrectionCue,
    analysis.correct?.terrainOrTask,
    analysis.train?.notes,
  ].join(' ').toLowerCase();

  if (includesKeyword(text, ['terrain', 'steeper', 'variable', 'uneven', 'confidence on'])) {
    issues.push('limited_terrain_adaptation');
  }
  if (includesKeyword(text, ['steer with legs', 'steer from the legs', 'lower body steering'])) {
    issues.push('weak_lower_body_steering');
  }
  if (includesKeyword(text, ['edge commitment', 'commit to the edge', 'washed', 'wash'])) {
    issues.push('limited_edge_commitment');
  }

  return unique(issues);
}

function normalizeSkiStrengths(analysis) {
  const strengths = [];
  const impression = String(analysis.detect?.overallImpression || '').toLowerCase();
  const terrainKey = detectTerrainComfortKey(analysis.detect?.terrainComfort);

  if (includesKeyword(impression, ['comfortable', 'confident', 'solid', 'consistent'])) {
    strengths.push('confidence_and_control');
  }
  if (terrainKey === 'blue_groomers') strengths.push('comfort_on_blue_groomers');
  if (terrainKey === 'green_groomers') strengths.push('comfort_on_green_groomers');
  if (terrainKey === 'red_terrain') strengths.push('comfort_on_red_terrain');
  if (includesKeyword(impression, ['outside ski', 'strong platform'])) {
    strengths.push('commitment_to_outside_ski');
  }

  return unique(strengths);
}

function normalizeSnowboardStrengths(analysis) {
  const strengths = [];
  const text = [
    analysis.detect?.bigPictureIssue,
    analysis.cause?.movementDescription,
    analysis.effect?.effectDescription,
    analysis.correct?.movementCorrectionCue,
    analysis.correct?.terrainOrTask,
  ].join(' ').toLowerCase();

  if (includesKeyword(text, ['calm', 'smooth', 'patient'])) strengths.push('calm_board_flow');
  if (includesKeyword(text, ['stacked', 'centered', 'balanced'])) strengths.push('balanced_edge_change');
  if (includesKeyword(text, ['comfortable', 'confident'])) strengths.push('confidence_and_control');
  if (includesKeyword(text, ['centered and mobile', 'mobile position', 'centered position'])) {
    strengths.push('centered_mobile_position');
  }
  if (includesKeyword(text, ['terrain', 'variable snow', 'steeper terrain', 'adapt'])) {
    strengths.push('terrain_adaptation_confidence');
  }

  return unique(strengths);
}

function detectSnowboardGoalThemes(text) {
  const normalized = String(text || '').toLowerCase();
  const matches = [];

  if (includesKeyword(normalized, ['quiet upper body', 'quieter upper body', 'quiet shoulders', 'quiet torso'])) {
    matches.push('quieter_upper_body');
  }
  if (includesKeyword(normalized, ['patient transition', 'calm transition', 'slow the transition'])) {
    matches.push('patient_transition');
  }
  if (includesKeyword(normalized, ['stacked', 'aligned', 'alignment through the body'])) {
    matches.push('stacked_alignment');
  }
  if (includesKeyword(normalized, ['centered', 'center over the board', 'more centered stance'])) {
    matches.push('centered_stance');
  }
  if (includesKeyword(normalized, ['lower body steering', 'steer with the legs', 'steer from the lower body', 'hips and knees'])) {
    matches.push('lower_body_steering');
  }
  if (includesKeyword(normalized, ['edge commitment', 'commit to the edge', 'trust the edge'])) {
    matches.push('stronger_edge_commitment');
  }
  if (includesKeyword(normalized, ['rounder turn shape', 'round turn shape', 'better symmetry', 'full arc'])) {
    matches.push('rounder_turn_shape');
  }
  if (includesKeyword(normalized, ['terrain adaptation', 'terrain confidence', 'adapt to terrain', 'variable terrain'])) {
    matches.push('terrain_adaptation_confidence');
  }
  if (includesKeyword(normalized, ['pressure management', 'manage pressure', 'flexion and extension', 'smoother pressure'])) {
    matches.push('smoother_pressure_management');
  }

  return unique(matches);
}

function normalizeSnowboardSignals(signals = {}) {
  if (!signals) return null;

  return {
    ...signals,
    issues: normalizeSignalEntries(signals.issues, SNOWBOARD_ISSUE_LABELS),
    strengths: normalizeSignalEntries(signals.strengths, SNOWBOARD_STRENGTH_LABELS),
    goals: normalizeSignalEntries(signals.goals, SNOWBOARD_GOAL_LABELS, SNOWBOARD_SIGNAL_ALIASES.goals),
  };
}

export function extractProgressSignals(analysis) {
  if (!analysis?.sport || !analysis?.variant) {
    return null;
  }

  if (analysis.variant === SAS_SKI_ANALYSIS_VARIANT) {
    const issueKeys = normalizeSkiIssues(analysis);
    const goalKeys = detectGoalThemes([
      analysis.correct?.compareToIdeal,
      analysis.correct?.developmentGoal,
      analysis.correct?.stanceCorrectionCue,
      analysis.correct?.movementCorrectionCue,
      analysis.correct?.feelOrAnalogy,
      analysis.train?.stepByStepPlan,
      analysis.train?.mileageGoal,
    ].join(' '));
    const strengthKeys = normalizeSkiStrengths(analysis);
    const terrainComfort = detectTerrainComfortKey(analysis.detect?.terrainComfort);

    return {
      sport: 'skiing',
      issues: buildSignalList(issueKeys, SKI_ISSUE_LABELS),
      strengths: buildSignalList(strengthKeys, SKI_STRENGTH_LABELS),
      goals: buildSignalList(goalKeys, SKI_GOAL_LABELS),
      terrainComfort,
      metadata: {
        turnType: String(analysis.detect?.turnType || '').trim(),
        turnShape: String(analysis.detect?.turnShape || '').trim(),
        turnPhase: String(analysis.detect?.turnPhase || '').trim(),
      },
    };
  }

  if (analysis.variant === SAS_SNOWBOARD_ANALYSIS_VARIANT) {
    const terrainFocus = detectTerrainComfortKey(
      [analysis.correct?.terrainOrTask, analysis.train?.notes].filter(Boolean).join(' ')
    );
    const issueKeys = normalizeSnowboardIssues(analysis);
    const goalKeys = detectSnowboardGoalThemes([
      analysis.correct?.stanceCorrectionCue,
      analysis.correct?.movementCorrectionCue,
      analysis.correct?.feelOrAnalogy,
      analysis.correct?.terrainOrTask,
      analysis.correct?.mileageGoal,
      analysis.train?.notes,
    ].join(' '));
    const strengthKeys = normalizeSnowboardStrengths(analysis);

    return {
      sport: 'snowboarding',
      issues: buildSignalList(issueKeys, SNOWBOARD_ISSUE_LABELS),
      strengths: buildSignalList(strengthKeys, SNOWBOARD_STRENGTH_LABELS),
      goals: buildSignalList(goalKeys, SNOWBOARD_GOAL_LABELS),
      terrainComfort: '',
      metadata: {
        turnSide: String(analysis.detect?.turnSide || '').trim(),
        turnPhase: String(analysis.detect?.turnPhase || '').trim(),
        terrainFocus,
      },
    };
  }

  return null;
}

// Pass 76 — signal precedence is CANONICAL: `analysis.signals` is the canonical derived projection;
// `analysis.derivedSignals` is a read-compatibility alias (the writer sets both to the SAME value — see
// normalizeReviewAnalysis / sanitizeReviewAnalysis). Readers MUST prefer `signals`, fall back to
// `derivedSignals`, then derive from the structured analysis. Never treat the two as separately meaningful.
export function getAnalysisSignals(analysis) {
  if (!analysis) return null;
  const signals = analysis.signals || analysis.derivedSignals || extractProgressSignals(analysis) || null;
  if (!signals) return null;
  if (analysis.variant === SAS_SNOWBOARD_ANALYSIS_VARIANT || analysis.sport === 'snowboarding') {
    return normalizeSnowboardSignals(signals);
  }
  return signals;
}

// Pass 76 — ONE canonical, PURE student-facing read model for structured HUMAN movement analysis. It is the
// single place that turns a canonical `video.analysis` (+ optional published `video.aiReview`) into displayable
// data, so the student review UI never re-derives sport-specific field names or confuses AI with human analysis.
//   · HUMAN analysis is canonical and independent of AI.
//   · Structured analysis is student-facing ONLY for a genuinely reviewed video (§5) — a pending coach draft is
//     never presented as published.
//   · Unknown/unsupported sport degrades safely (hasHumanAnalysis:false, sections:[]) — it never fabricates ski
//     or snowboard structure.
//   · publishedAi is returned SEPARATELY and only when actually published (reviewed + video.aiReview present);
//     private drafts never reach here (they don't live on the video doc).
function humanizeStanceKey(key) {
  return String(key || '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

function buildAnalysisSectionView(analysis, sectionKey, title, sectionConfig, labelResolver) {
  const data = analysis?.[sectionKey] || {};
  const inline = (sectionConfig?.inlineFields || [])
    .map((key) => String(data?.[key] || '').trim())
    .filter(Boolean)
    .join(' • ');
  const stanceIssues = sectionConfig?.showStanceIssues
    ? Object.entries(data?.stanceIssues || {})
        .map(([key, value]) => ({ label: humanizeStanceKey(key), value: String(value || '').trim() }))
        .filter((item) => item.value)
    : [];
  const chips = sectionConfig?.chipField?.key
    ? (data?.[sectionConfig.chipField.key] || [])
        .map((value) => labelResolver(sectionConfig.chipField.labelType, value))
        .filter(Boolean)
    : [];
  const texts = (sectionConfig?.textFields || [])
    .map((field) => String(data?.[field] || '').trim())
    .filter(Boolean);
  const isEmpty = !inline && !stanceIssues.length && !chips.length && !texts.length;
  return { key: sectionKey, title: title || sectionKey, inline, stanceIssues, chips, texts, isEmpty };
}

export function buildMovementAnalysisView(video) {
  const reviewed = video?.status === 'reviewed';
  const rawAnalysis = video?.analysis || null;
  const analysis = rawAnalysis ? normalizeReviewAnalysis(video, rawAnalysis) : null;
  // Human analysis is student-facing ONLY once the video is genuinely reviewed AND the analysis is meaningful.
  const hasHumanAnalysis = reviewed && !!analysis && hasMeaningfulAnalysis(analysis);
  // The published AI supplement — ONLY when reviewed and actually published. Never a private draft.
  const publishedAi = reviewed ? (video?.aiReview || null) : null;

  if (!hasHumanAnalysis) {
    return {
      reviewed,
      hasHumanAnalysis: false,
      sport: analysis?.sport || null,
      variant: analysis?.variant || null,
      display: null,
      sections: [],
      signals: null,
      publishedAi,
    };
  }

  const display = getAnalysisDisplayMeta(analysis.variant);
  const labelResolver = (type, value) => {
    if (isSnowboardAnalysisVariant(analysis)) return getSnowboardAnalysisLabel(type, value);
    if (isSkiAnalysisVariant(analysis)) return getSkiAnalysisLabel(type, value);
    return String(value || '').replace(/_/g, ' ');
  };

  const sections = ['detect', 'cause', 'effect', 'correct', 'train']
    .map((key) => buildAnalysisSectionView(
      analysis, key, display?.studentSections?.[key], display?.studentFields?.[key], labelResolver,
    ))
    .filter((section) => !section.isEmpty);

  return {
    reviewed,
    hasHumanAnalysis: true,
    sport: analysis.sport,
    variant: analysis.variant,
    display,
    sections,
    signals: getAnalysisSignals(analysis) || null,
    publishedAi,
  };
}

export function isSnowboardVideo(videoOrSport) {
  const sport = typeof videoOrSport === 'string' ? videoOrSport : videoOrSport?.sport;
  return normalizeSportName(sport) === 'snowboarding';
}

export function isSkiVideo(videoOrSport) {
  const sport = typeof videoOrSport === 'string' ? videoOrSport : videoOrSport?.sport;
  return normalizeSportName(sport) === 'skiing';
}

export function createEmptySnowboardAnalysis() {
  return createEmptyAnalysisForVariant(SAS_SNOWBOARD_ANALYSIS_VARIANT);
}

export function createEmptySkiAnalysis() {
  return createEmptyAnalysisForVariant(SAS_SKI_ANALYSIS_VARIANT);
}

function getAnalysisBase(videoOrSport, analysis) {
  const variantId = analysis?.variant;
  const definition = getAnalysisVariantForSport(videoOrSport, variantId);
  if (!definition) return null;
  return createEmptyAnalysisForVariant(definition.id);
}

export function normalizeReviewAnalysis(video, analysis) {
  const base = getAnalysisBase(video, analysis);
  if (!base) {
    return analysis || null;
  }

  const input = analysis || {};
  const normalized = {
    ...base,
    framework: input.framework || base.framework,
    sport: base.sport,
    variant: input.variant || base.variant,
    detect: {
      ...base.detect,
      ...(input.detect || {}),
    },
    cause: {
      ...base.cause,
      ...(input.cause || {}),
      stanceIssues: {
        ...base.cause.stanceIssues,
        ...(input.cause?.stanceIssues || {}),
      },
    },
    effect: {
      ...base.effect,
      ...(input.effect || {}),
    },
    correct: {
      ...base.correct,
      ...(input.correct || {}),
    },
    train: {
      ...base.train,
      ...(input.train || {}),
    },
  };

  if (normalized.variant === SAS_SNOWBOARD_ANALYSIS_VARIANT) {
    normalized.detect.observedInefficiencies = cleanArray(input.detect?.observedInefficiencies);
    normalized.effect.boardPerformanceIssues = cleanArray(input.effect?.boardPerformanceIssues);
  }

  if (normalized.variant === SAS_SKI_ANALYSIS_VARIANT) {
    normalized.detect.primaryPerformanceIssues = cleanArray(input.detect?.primaryPerformanceIssues);
    normalized.effect.skiPerformanceIssues = cleanArray(input.effect?.skiPerformanceIssues);
  }

  const rawSignals = input.signals || input.derivedSignals || extractProgressSignals(normalized);
  const normalizedSignals = normalized.variant === SAS_SNOWBOARD_ANALYSIS_VARIANT
    ? normalizeSnowboardSignals(rawSignals)
    : rawSignals;
  normalized.signals = normalizedSignals;
  normalized.derivedSignals = normalizedSignals;

  return normalized;
}

export function sanitizeReviewAnalysis(analysis) {
  if (!analysis) {
    return null;
  }

  const normalized = normalizeReviewAnalysis({ sport: analysis.sport }, analysis);
  if (!normalized) {
    return null;
  }

  if (normalized.variant === SAS_SNOWBOARD_ANALYSIS_VARIANT) {
    const sanitized = {
      ...normalized,
      detect: {
        ...normalized.detect,
        turnSide: String(normalized.detect.turnSide || '').trim(),
        turnPhase: String(normalized.detect.turnPhase || '').trim(),
        bigPictureIssue: String(normalized.detect.bigPictureIssue || '').trim(),
        observedInefficiencies: cleanArray(normalized.detect.observedInefficiencies),
        edgePressureSteerObservation: String(normalized.detect.edgePressureSteerObservation || '').trim(),
      },
      cause: {
        ...normalized.cause,
        movementDescription: String(normalized.cause.movementDescription || '').trim(),
        stanceIssues: cleanObjectValues(normalized.cause.stanceIssues),
      },
      effect: {
        ...normalized.effect,
        boardPerformanceIssues: cleanArray(normalized.effect.boardPerformanceIssues),
        effectDescription: String(normalized.effect.effectDescription || '').trim(),
      },
      correct: {
        ...normalized.correct,
        stanceCorrectionCue: String(normalized.correct.stanceCorrectionCue || '').trim(),
        movementCorrectionCue: String(normalized.correct.movementCorrectionCue || '').trim(),
        feelOrAnalogy: String(normalized.correct.feelOrAnalogy || '').trim(),
        terrainOrTask: String(normalized.correct.terrainOrTask || '').trim(),
        mileageGoal: String(normalized.correct.mileageGoal || '').trim(),
      },
      train: {
        ...normalized.train,
        notes: String(normalized.train.notes || '').trim(),
      },
    };
    return {
      ...sanitized,
      signals: extractProgressSignals(sanitized),
      derivedSignals: extractProgressSignals(sanitized),
    };
  }

  if (normalized.variant === SAS_SKI_ANALYSIS_VARIANT) {
    const sanitized = {
      ...normalized,
      detect: {
        ...normalized.detect,
        overallImpression: String(normalized.detect.overallImpression || '').trim(),
        terrainComfort: String(normalized.detect.terrainComfort || '').trim(),
        turnType: String(normalized.detect.turnType || '').trim(),
        turnShape: String(normalized.detect.turnShape || '').trim(),
        turnPhase: String(normalized.detect.turnPhase || '').trim(),
        primaryPerformanceIssues: cleanArray(normalized.detect.primaryPerformanceIssues),
      },
      cause: {
        ...normalized.cause,
        movementDescription: String(normalized.cause.movementDescription || '').trim(),
        stanceIssues: cleanObjectValues(normalized.cause.stanceIssues),
      },
      effect: {
        ...normalized.effect,
        skiPerformanceIssues: cleanArray(normalized.effect.skiPerformanceIssues),
        effectDescription: String(normalized.effect.effectDescription || '').trim(),
      },
      correct: {
        ...normalized.correct,
        compareToIdeal: String(normalized.correct.compareToIdeal || '').trim(),
        developmentGoal: String(normalized.correct.developmentGoal || '').trim(),
        stanceCorrectionCue: String(normalized.correct.stanceCorrectionCue || '').trim(),
        movementCorrectionCue: String(normalized.correct.movementCorrectionCue || '').trim(),
        feelOrAnalogy: String(normalized.correct.feelOrAnalogy || '').trim(),
      },
      train: {
        ...normalized.train,
        terrainOrTask: String(normalized.train.terrainOrTask || '').trim(),
        stepByStepPlan: String(normalized.train.stepByStepPlan || '').trim(),
        mileageGoal: String(normalized.train.mileageGoal || '').trim(),
      },
    };
    return {
      ...sanitized,
      signals: extractProgressSignals(sanitized),
      derivedSignals: extractProgressSignals(sanitized),
    };
  }

  return normalized;
}

export function hasMeaningfulAnalysis(analysis) {
  if (!analysis) {
    return false;
  }

  if (analysis.variant === SAS_SNOWBOARD_ANALYSIS_VARIANT) {
    const values = [
      analysis.detect?.turnSide,
      analysis.detect?.turnPhase,
      analysis.detect?.bigPictureIssue,
      ...(analysis.detect?.observedInefficiencies || []),
      analysis.detect?.edgePressureSteerObservation,
      ...Object.values(analysis.cause?.stanceIssues || {}),
      analysis.cause?.movementDescription,
      ...(analysis.effect?.boardPerformanceIssues || []),
      analysis.effect?.effectDescription,
      analysis.correct?.stanceCorrectionCue,
      analysis.correct?.movementCorrectionCue,
      analysis.correct?.feelOrAnalogy,
      analysis.correct?.terrainOrTask,
      analysis.correct?.mileageGoal,
      analysis.train?.notes,
    ];
    return values.some((value) => String(value || '').trim());
  }

  if (analysis.variant === SAS_SKI_ANALYSIS_VARIANT) {
    const values = [
      analysis.detect?.overallImpression,
      analysis.detect?.terrainComfort,
      analysis.detect?.turnType,
      analysis.detect?.turnShape,
      analysis.detect?.turnPhase,
      ...(analysis.detect?.primaryPerformanceIssues || []),
      ...Object.values(analysis.cause?.stanceIssues || {}),
      analysis.cause?.movementDescription,
      ...(analysis.effect?.skiPerformanceIssues || []),
      analysis.effect?.effectDescription,
      analysis.correct?.compareToIdeal,
      analysis.correct?.developmentGoal,
      analysis.correct?.stanceCorrectionCue,
      analysis.correct?.movementCorrectionCue,
      analysis.correct?.feelOrAnalogy,
      analysis.train?.terrainOrTask,
      analysis.train?.stepByStepPlan,
      analysis.train?.mileageGoal,
    ];
    return values.some((value) => String(value || '').trim());
  }

  return false;
}

export function isSnowboardAnalysisVariant(analysis) {
  return analysis?.variant === SAS_SNOWBOARD_ANALYSIS_VARIANT;
}

export function isSkiAnalysisVariant(analysis) {
  return analysis?.variant === SAS_SKI_ANALYSIS_VARIANT;
}

export function getSnowboardAnalysisLabel(type, value) {
  const optionType = type === 'boardPerformance' ? 'boardPerformance' : 'inefficiency';
  return getVariantOptionLabel(SAS_SNOWBOARD_ANALYSIS_VARIANT, optionType, value);
}

export function getSkiAnalysisLabel(type, value) {
  const optionType = type === 'performance' ? 'performance' : 'primaryPerformance';
  return getVariantOptionLabel(SAS_SKI_ANALYSIS_VARIANT, optionType, value);
}
